import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Send, RefreshCw, X, FileSignature, Clock, Check, AlertTriangle, User, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAppDialog } from '@/hooks/useAppDialog';

const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-800',
  SENT: 'bg-blue-100 text-blue-800',
  VIEWED: 'bg-yellow-100 text-yellow-800',
  SIGNED: 'bg-green-100 text-green-800',
  DECLINED: 'bg-red-100 text-red-800',
  EXPIRED: 'bg-gray-100 text-gray-500',
};

export default function ESignRequestsPage() {
  const { dangerConfirm } = useAppDialog();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTargetType, setFilterTargetType] = useState('');
  const [showSend, setShowSend] = useState(false);
  const [stats, setStats] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, pageSize: 20 });
      if (filterStatus) params.append('status', filterStatus);
      if (filterTargetType) params.append('targetType', filterTargetType);
      const res = await api.get(`/esign/requests?${params}`);
      setRequests(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load e-sign requests');
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, filterTargetType]);

  const fetchStats = async () => {
    try {
      const res = await api.get('/esign/stats/summary');
      setStats(res.data.data);
    } catch (err) {}
  };

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, [fetchRequests]);

  const handleResend = async (reqId) => {
    // Resend is just sending a reminder - for now we can show as info
    toast.info('Reminder sent (feature placeholder)');
  };

  const handleCancel = async (reqId) => {
    const ok = await dangerConfirm({
      title: 'Cancel signature request?',
      description: 'The recipient will no longer be able to sign this document. This cannot be undone.',
      confirmLabel: 'Cancel request',
      cancelLabel: 'Keep request',
      testId: 'esign-cancel-confirm',
    });
    if (!ok) return;
    try {
      await api.post(`/esign/${reqId}/cancel`);
      toast.success('Request cancelled');
      fetchRequests();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to cancel request');
    }
  };

  return (
    <div className="space-y-6" data-testid="esign-requests-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">E-Sign Requests</h1>
          <p className="text-muted-foreground">Manage electronic signature requests</p>
        </div>
        <Dialog open={showSend} onOpenChange={setShowSend}>
          <DialogTrigger asChild>
            <Button data-testid="send-esign-btn"><Send className="h-4 w-4 mr-2" />Send for Signature</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Send Document for Signature</DialogTitle></DialogHeader>
            <SendESignForm onSuccess={() => { setShowSend(false); fetchRequests(); fetchStats(); }} />
          </DialogContent>
        </Dialog>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Pending" value={stats.pending} icon={Clock} color="text-blue-600" />
          <StatCard label="Viewed" value={stats.viewed} icon={FileText} color="text-yellow-600" />
          <StatCard label="Signed" value={stats.signed} icon={Check} color="text-green-600" />
          <StatCard label="Declined" value={stats.declined} icon={X} color="text-red-600" />
          <StatCard label="Expired" value={stats.expired} icon={AlertTriangle} color="text-gray-500" />
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex gap-3 items-center">
            <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-[150px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="SENT">Sent</SelectItem>
                <SelectItem value="VIEWED">Viewed</SelectItem>
                <SelectItem value="SIGNED">Signed</SelectItem>
                <SelectItem value="DECLINED">Declined</SelectItem>
                <SelectItem value="EXPIRED">Expired</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterTargetType} onValueChange={(v) => { setFilterTargetType(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-[150px]" data-testid="filter-target-type"><SelectValue placeholder="Target Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="STAFF">Staff</SelectItem>
                <SelectItem value="PARTICIPANT">Participant</SelectItem>
                <SelectItem value="PORTAL_USER">Portal User</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Document</TableHead>
                <TableHead>Target User</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sent Date</TableHead>
                <TableHead>Signed Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8">Loading...</TableCell></TableRow>
              ) : requests.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No e-sign requests found</TableCell></TableRow>
              ) : requests.map(req => (
                <TableRow key={req.id} className="hover:bg-muted/50" data-testid={`esign-row-${req.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileSignature className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium truncate max-w-[200px]">{req.documentTitle || 'Document'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-sm">{req.targetName || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">{req.targetEmail}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge status={req.targetType} /></TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[req.status] || ''}`}>
                      {req.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{req.sentAt?.slice(0, 10) || '-'}</TableCell>
                  <TableCell className="text-sm">{req.signedAt?.slice(0, 10) || '-'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {(req.status === 'SENT' || req.status === 'VIEWED') && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => handleResend(req.id)} title="Resend">
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleCancel(req.id)} title="Cancel" className="text-red-600">
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {total > 20 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">Page {page} of {Math.ceil(total / 20)}</span>
          <Button variant="outline" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color = 'text-primary' }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-muted ${color}`}><Icon className="h-5 w-5" /></div>
        <div>
          <p className="text-2xl font-bold">{value || 0}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function SendESignForm({ onSuccess }) {
  const [form, setForm] = useState({ documentId: '', targetType: '', targetUserId: '', message: '' });
  const [documents, setDocuments] = useState([]);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/documents?pageSize=100&status=ACTIVE').then(r => setDocuments(r.data.items || []));
  }, []);

  useEffect(() => {
    if (form.targetType === 'STAFF') {
      api.get('/staff?pageSize=100').then(r => setTargets(r.data.items?.map(s => ({ id: s.id, name: s.user_full_name || s.full_name || 'Staff' })) || []));
    } else if (form.targetType === 'PARTICIPANT') {
      api.get('/participants?pageSize=100').then(r => setTargets(r.data.items?.map(p => ({ id: p.id, name: `${p.first_name} ${p.last_name}` })) || []));
    } else if (form.targetType === 'PORTAL_USER') {
      api.get('/portal-users?pageSize=100').then(r => setTargets(r.data.items?.map(u => ({ id: u.id, name: `${u.firstName} ${u.lastName}` })) || [])).catch(() => setTargets([]));
    } else {
      setTargets([]);
    }
  }, [form.targetType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.documentId || !form.targetType || !form.targetUserId) {
      toast.error('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      await api.post('/esign/send', {
        documentId: form.documentId,
        targetType: form.targetType,
        targetUserId: form.targetUserId,
        message: form.message || undefined
      });
      toast.success('E-sign request sent successfully');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to send e-sign request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Document *</Label>
        <Select value={form.documentId} onValueChange={(v) => setForm(f => ({ ...f, documentId: v }))}>
          <SelectTrigger><SelectValue placeholder="Select document" /></SelectTrigger>
          <SelectContent>
            {documents.map(d => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Target Type *</Label>
        <Select value={form.targetType} onValueChange={(v) => setForm(f => ({ ...f, targetType: v, targetUserId: '' }))}>
          <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="STAFF">Staff</SelectItem>
            <SelectItem value="PARTICIPANT">Participant</SelectItem>
            <SelectItem value="PORTAL_USER">Portal User</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Target User *</Label>
        <Select value={form.targetUserId} onValueChange={(v) => setForm(f => ({ ...f, targetUserId: v }))} disabled={!form.targetType || targets.length === 0}>
          <SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
          <SelectContent>
            {targets.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Message (optional)</Label>
        <Textarea value={form.message} onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Optional message to recipient" rows={3} />
      </div>
      <Button type="submit" disabled={loading} className="w-full">{loading ? 'Sending...' : 'Send for Signature'}</Button>
    </form>
  );
}
